import { Box, Divider, Grid, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import { Add, Clear } from "@mui/icons-material";
import CommonDialog from "../../common/components/CommonDialog";
import { Academics, ActivitiesStatus, DisplayGrade, Durations, ProficiencyLevels, StudentGrade, UserTypeId } from "../../common/const";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { createCodingSkill, getCodingSkills, updateCodingSkill } from "../../api/codingSKillApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import CommonGradeButton from "./CommonGradeButton";

const CodingInfo = () => {
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.auth);
    const [currentGrade, setCurrentGrade] = useState(user?.student_current_grade);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [skills, setSkills] = useState([]);
    const [formData, setFormData] = useState({
        coding: {
            status: Academics[0].value,
            courseName: '',
            proficiencyLevel: '',
            examTakenDate: '',
            codingStatus: '',
            duration: '',
            startdate: '',
            enddate: '',
            certificate: ''
        }
    });
    const [existingDocs, setExistingDocs] = useState([]);

    const validateField = (name, value, file) => {
        switch (name) {
            case 'courseName':
                if (!value) return 'Course/Class Name is required';
                return '';
            case 'proficiencyLevel':
                if (!value) return 'Proficiency Level is required';
                return '';
            case 'status':
                if (!value) return 'Status is required';
                return '';
            case 'certificate': // Assuming this is your file input name
                if (file) {
                    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                    if (file.size > maxSize) {
                        return 'File size must be less than 5MB';
                    }
                }
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (files && files.length > 0) {
            const file = files;

            // Validate file size immediately
            const error = validateField(name, '', file); // Pass empty string for value, file for validation

            setFormData((prev) => ({
                ...prev,
                coding: {
                    ...prev.coding,
                    [name]: file,
                },
            }));

            // Set or clear error
            if (error) {
                setErrors(prev => ({ ...prev, [name]: error }));
            } else {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                coding: {
                    ...prev.coding,
                    [name]: value,
                },
            }));
        }

        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData.coding).forEach(key => {
            const error = validateField(key, formData.coding[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            const formDataToSend = new FormData();

            // Add each property to FormData
            formDataToSend.append('course_name', formData.coding.courseName);
            formDataToSend.append('proficiency_level', formData.coding.proficiencyLevel);
            formDataToSend.append('exam_taken_date', formData.coding.examTakenDate !== '' ? new Date(formData.coding.examTakenDate).toISOString() : null);
            formDataToSend.append('skill_status', formData.coding.codingStatus);
            formDataToSend.append('status', formData.coding.status);
            formDataToSend.append('duration', formData.coding.duration || 0);
            formDataToSend.append('start_date', formData.coding.startdate !== '' ? new Date(formData.coding.startdate).toISOString() : null);
            formDataToSend.append('end_date', formData.coding.enddate !== '' ? new Date(formData.coding.enddate).toISOString() : null);
            formDataToSend.append('student_grade', currentGrade);
            formDataToSend.append('student_id', user.student_id);
            if (formData.coding.certificate && formData.coding.certificate.length > 0) {
                const files = Array.from(formData.coding.certificate);
                files.forEach((file, index) => {
                    formDataToSend.append('documents', file);
                });
            }
            if (existingDocs?.length > 0) {
                formDataToSend.append('documents', JSON.stringify(existingDocs));
            }
            let subjecResponse;
            dispatch(setLoading({
                loading: true
            }));
            if (formData.coding?.skill_id && formData.coding?.skill_id !== '') {
                subjecResponse = await updateCodingSkill(formData.coding?.skill_id, formDataToSend);
            } else {
                subjecResponse = await createCodingSkill(formDataToSend);
            }
            if (subjecResponse) {
                dispatch(setLoading({
                    loading: false
                }));
                if (formData.coding?.skill_id !== '') {
                    showNotification('Coding/Skill added successfully', 'success');
                } else {
                    showNotification('Coding/Skill updated successfully', 'success');
                }
                setExistingDocs(null);
                setDialogOpen(false);
                getCoding();
                setDialogOpen(false);
                setFormData({
                    coding: {
                        skill_id: '',
                        status: Academics[0].value,
                        courseName: '',
                        proficiencyLevel: '',
                        examTakenDate: '',
                        codingStatus: '',
                        duration: '',
                        startdate: '',
                        enddate: '',
                        certificate: ''
                    }
                });
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    }

    const getCoding = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const subjectRes = await getCodingSkills(user?.student_id, currentGrade);
            if (currentGrade === null) {
                setCurrentGrade(subjectRes.data?.[0]?.student_grade || StudentGrade[0].value);
            }
            setSkills(subjectRes.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id) {
            getCoding();
        }
    }, [user?.student_id, currentGrade]);

    const getMinDate = () => {
        const date = formData.coding.startdate ? new Date(formData.coding.startdate) : new Date();
        date.setDate(date.getDate() + 1); // Add 7 days
        return date.toISOString().split('T')[0];
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={dialogOpen}
                size="sm"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{formData?.coding?.skill_id && formData?.coding?.skill_id !== '' ? 'Edit Coding/Technology/Skills' : 'Add Coding/Technology/Skills'}</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={1.5} mt={0.5} mb={2}>
                        <Grid item xs={12} md={12}>
                            <CommonSelect
                                label="Status"
                                name="status"
                                value={formData.coding.status}
                                onChange={handleInputChange}
                                placeholder="Select Status"
                                error={errors.status}
                                options={Academics}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommonFormField
                                label="Course/Class Name"
                                type="email"
                                name="courseName"
                                value={formData.coding.courseName}
                                onChange={handleInputChange}
                                error={errors.courseName}
                                placeholder="e.g., Python, Java, C++"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommonSelect
                                label="Proficiency Level"
                                name="proficiencyLevel"
                                value={formData.coding.proficiencyLevel}
                                onChange={handleInputChange}
                                placeholder="Select Level"
                                error={errors.proficiencyLevel}
                                options={ProficiencyLevels}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommonFormField
                                label="Exam Taken Date"
                                type="date"
                                name="examTakenDate"
                                value={formData.coding.examTakenDate}
                                onChange={handleInputChange}
                                placeholder="Select exam date"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommonSelect
                                label="Select Status"
                                name="codingStatus"
                                value={formData.coding.codingStatus}
                                onChange={handleInputChange}
                                placeholder="Select Status"
                                options={ActivitiesStatus}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <CommonSelect
                                label="Select Duration"
                                name="duration"
                                value={formData.coding.duration}
                                onChange={handleInputChange}
                                placeholder="Select month"
                                options={Durations}
                            />
                        </Grid>
                        <Grid item xs={12} md={formData.coding.status === 'PAST' ? 6 : 12}>
                            <CommonFormField
                                label="Start Date"
                                type="date"
                                name="startdate"
                                value={formData.coding.startdate}
                                onChange={handleInputChange}
                                error={errors.startdate}
                                placeholder="Select Start Date"
                            />
                        </Grid>
                        {formData.coding.status === 'PAST' && (
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="End Date"
                                    type="date"
                                    name="enddate"
                                    value={formData.coding.enddate}
                                    onChange={handleInputChange}
                                    placeholder="Select End Date"
                                    minDate={getMinDate()}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} md={12}>
                            <CommonFormField
                                label="Upload Certificate"
                                type="file"
                                name="certificate"
                                value={formData.coding.certificate?.[0]?.name}
                                onChange={handleInputChange}
                                placeholder="PDF"
                            />
                        </Grid>
                        <Grid item md={12}>
                            {!Array.isArray(existingDocs) ? JSON.parse(existingDocs)?.length > 0 && JSON.parse(existingDocs).map((i, index) => (
                                <Box
                                    padding={index === 0 ? "13px 13px 8px 13px" : "8px 13px"}
                                    display="flex"
                                    gap={1}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Box display="flex" gap={1} alignItems="center">
                                        <img src="/Images/pdficon.png" alt="pdf icon" width="30px" />
                                        <Typography fontSize={14} color={theme.palette.background.dark}>{i?.filename}</Typography>
                                    </Box>
                                    <Clear sx={{ color: theme.palette.background.dark, cursor: 'pointer' }} onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setExistingDocs(prevItems => prevItems.filter((_, i) => i !== index));
                                    }}
                                    />
                                </Box>
                            )) : existingDocs?.length > 0 && existingDocs.map((i, index) => (
                                <Box
                                    padding={index === 0 ? "13px 13px 8px 13px" : "8px 13px"}
                                    display="flex"
                                    gap={1}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Box display="flex" gap={1} alignItems="center">
                                        <img src="/Images/pdficon.png" alt="pdf icon" width="30px" />
                                        <Typography fontSize={14} color={theme.palette.background.dark}>{i?.filename}</Typography>
                                    </Box>
                                    <Clear sx={{ color: theme.palette.background.dark, cursor: 'pointer' }} onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setExistingDocs(prevItems => prevItems.filter((_, i) => i !== index));
                                    }}
                                    />
                                </Box>
                            ))}
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Cancel"
                            size="small"
                            background='white'
                            color={theme.palette.secondary.main}
                            border={`1px solid ${theme.palette.background.secondary}`}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={async () => {
                                setFormData({
                                    coding: {
                                        status: Academics[0].value,
                                        courseName: '',
                                        proficiencyLevel: '',
                                        examTakenDate: '',
                                        codingStatus: '',
                                        duration: '',
                                        startdate: '',
                                        enddate: '',
                                        certificate: ''
                                    }
                                });
                                setExistingDocs(null);
                                setErrors({});
                                setDialogOpen(false)
                            }}
                        />
                        <CommonButton
                            label={formData?.coding?.skill_id && formData?.coding?.skill_id !== '' ? 'Edit' : 'Add'}
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={() => {
                                handleSubmit();
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonCard
                borderRadius={1}
                border={`1px solid ${theme.palette.background.secondary}`}
                paddingBottom="0px"
                padding="0px"
                marginTop="0px"
                headerContent={
                    <Box>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                        >
                            <Box>
                                <Typography fontSize={22} fontWeight={600}>My Coding/Technology/Skills</Typography>
                                <Typography fontSize={15} color={theme.palette.background.dark}>Manage My Coding/Technology/Skills</Typography>
                            </Box>
                            {user?.user_type_id === UserTypeId.Student && (
                            <CommonButton
                                label="Add Coding/Technology/Skills"
                                width="270px"
                                marginBottom="0px"
                                fontWeight={600}
                                startIcon={<Add />}
                                handleClick={() => setDialogOpen(true)}
                            />)}
                        </Box>
                        <Divider />
                    </Box>
                }
                content={
                    <Grid container spacing={1} sx={{ padding: '13px', marginBottom: 1 }}>
                        <Grid item xs={12} md={12} display="flex" gap={1} mb={1}>
                            <CommonGradeButton
                                currentGrade={currentGrade}
                                setCurrentGrade={setCurrentGrade}
                            />
                        </Grid>
                        {skills.map((a, index) => (
                            <Grid item md={6}>
                                <CommonCard
                                    key={index}
                                    borderRadius={1}
                                    border={`1px solid ${theme.palette.background.secondary}`}
                                    paddingBottom="0px"
                                    padding="0px"
                                    marginTop="0px"
                                    content={
                                        <Fragment>
                                            <Box
                                                padding="13px"
                                            >
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Typography fontWeight={600}>{a.course_name}</Typography>
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                    >
                                                        {user?.user_type_id === UserTypeId.Student && (
                                                            <CommonButton
                                                                label="Edit"
                                                                background="white"
                                                                marginBottom="0px"
                                                                border={`1px solid ${theme.palette.background.secondary}`}
                                                                height="30px"
                                                                fontWeight={600}
                                                                width="50px"
                                                                handleClick={() => {
                                                                    setDialogOpen(true);
                                                                    setExistingDocs(a.documents);
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        coding: {
                                                                            ...prev.coding,
                                                                            skill_id: a.skill_id,
                                                                            status: a.status,
                                                                            courseName: a.course_name,
                                                                            proficiencyLevel: a.proficiency_level,
                                                                            examTakenDate: a.exam_taken_date,
                                                                            codingStatus: a.skill_status,
                                                                            duration: a.duration,
                                                                            startdate: a.start_date !== null ? moment(a.start_date)?.format('YYYY-MM-DD') : '',
                                                                            enddate: a.end_date !== null ? moment(a.end_date)?.format('YYYY-MM-DD') : ''
                                                                        },
                                                                    }));
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Typography color={theme.palette.background.dark} fontSize={14}>Exam Taken Date: <span style={{ fontWeight: 600, color: 'black' }}>{a.exam_taken_date ? moment(a.exam_taken_date).format('DD MMM, YYYY') : '-'}</span></Typography>
                                                <Box
                                                    display="flex"
                                                    gap={1}
                                                    mt={1}
                                                >
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        borderRadius={0.6}
                                                        padding="0px 5px"
                                                        sx={{
                                                            background: theme.palette.primary.main
                                                        }}
                                                    >
                                                        <Typography fontSize={14} color={theme.palette.secondary.main}>{a.proficiency_level ? ProficiencyLevels.find((i) => i.value === a?.proficiency_level)?.label : '-'}</Typography>
                                                    </Box>
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        borderRadius={0.6}
                                                        padding="0px 5px"
                                                    >
                                                        <Typography fontSize={14}>{DisplayGrade[currentGrade]}</Typography>
                                                    </Box>
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        borderRadius={0.6}
                                                        padding="0px 5px"
                                                    >
                                                        <Typography fontSize={14}>{a.status ? Academics.find((i) => i.value === a.status)?.label : '-'}</Typography>
                                                    </Box>
                                                    <Box
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        borderRadius={0.6}
                                                        padding="0px 5px"
                                                        sx={{
                                                            background: a.skill_status === 'INPROGRESS' ? theme.palette.secondary.main : '#01875F',
                                                        }}
                                                    >
                                                        <Typography fontSize={14} color={a.skill_status === 'INPROGRESS' ? theme.palette.primary.main : 'white'}>{a.skill_status ? ActivitiesStatus.find((i) => i.value === a.skill_status)?.label : '-'}</Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    display="flex"
                                                    gap={10}
                                                    mt={1}
                                                    mb={1}
                                                >
                                                    <Box>
                                                        <Typography fontSize={14} fontWeight={600}>{a.start_date ? moment(a.start_date).format('DD MMM, YYYY') : '-'}</Typography>
                                                        <Typography fontSize={13} color={theme.palette.background.dark}>Start Date</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography fontSize={14} fontWeight={600}>{a.end_date ? moment(a.end_date).format('DD MMM, YYYY') : '-'}</Typography>
                                                        <Typography fontSize={13} color={theme.palette.background.dark}>End Date</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography fontSize={14} fontWeight={600}>{a.duration ? `${Durations.find((i) => i.value === a.duration)?.label}` : '-'}</Typography>
                                                        <Typography fontSize={13} color={theme.palette.background.dark}>Duration Month</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Divider />
                                            {!Array.isArray(a.documents) ? JSON.parse(a.documents)?.length > 0 && JSON.parse(a.documents).map((i, index) => (
                                                <Box
                                                    padding={index === 0 ? "13px 13px 8px 13px" : "8px 13px"}
                                                    display="flex"
                                                    gap={1}
                                                    alignItems="center"
                                                >
                                                    <img src="/Images/pdficon.png" alt="pdf icon" width="30px" />
                                                    <Typography fontSize={14} color={theme.palette.background.dark}>{i?.filename}</Typography>
                                                </Box>
                                            )) : a.documents?.length > 0 && a.documents.map((i, index) => (
                                                <Box
                                                    padding={index === 0 ? "13px 13px 8px 13px" : "8px 13px"}
                                                    display="flex"
                                                    gap={1}
                                                    alignItems="center"
                                                >
                                                    <img src="/Images/pdficon.png" alt="pdf icon" width="30px" />
                                                    <Typography fontSize={14} color={theme.palette.background.dark}>{i?.filename}</Typography>
                                                </Box>
                                            ))}
                                        </Fragment>
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                }
            />
        </Fragment>
    )
}

export default CodingInfo;